import type { ListSpacesParams } from '@cocoonspace/sdk-js/types/listSpacesParams'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useListSpaces } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import {
	Carousel,
	CarouselContent,
	CarouselIndicators,
	CarouselItem,
} from '@cocoonspace/ui/components/carousel'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useSearchParams } from 'next/navigation'

import { SpaceCard } from '~/domains/search/components/space-card'

interface DynamicCarouselProps {
	filters: ListSpacesParams
}

interface StaticCarouselProps {
	spaces: Space[]
}

export type SpaceCarouselProps = {
	slidesToScrollXs?: number
	slidesToScrollSm?: 1 | 2 | 3
	slidesToScrollMd?: 1 | 2 | 3 | 4
	className?: string
	onCardClick?: (space: Space) => void
} & (DynamicCarouselProps | StaticCarouselProps)

const ItemsCarousel = ({
	spaces,
	slidesToScrollXs = 1,
	slidesToScrollSm = 2,
	slidesToScrollMd = 3,
	className,
	onCardClick,
}: Omit<SpaceCarouselProps, 'filters'> & StaticCarouselProps) => {
	const searchParams = useSearchParams()
	const currentSearch = searchParams.toString()

	return (
		<Carousel
			className={cn('flex flex-col gap-4', className)}
			opts={{
				breakpoints: {
					'(max-width: 640px)': {
						align: 'center',
						skipSnaps: false,
						slidesToScroll: slidesToScrollXs,
					},
					'(min-width: 640px)': {
						align: 'start',
						slidesToScroll: slidesToScrollSm,
					},
					'(min-width: 768px)': {
						slidesToScroll: slidesToScrollMd,
					},
				},
			}}
		>
			<CarouselContent>
				{spaces.map((space) => (
					<CarouselItem
						className='sm:basis-1/2 md:basis-1/3'
						key={space.id}
					>
						<div className='p-[2px]'>
							<SpaceCard
								draggable={false}
								href={`${`/${space.zone}/${space.slug}`}${currentSearch ? `?${currentSearch}` : ''}`}
								onClick={() => onCardClick?.(space)}
								space={space}
							/>
						</div>
					</CarouselItem>
				))}
			</CarouselContent>

			<CarouselIndicators />
		</Carousel>
	)
}

const StaticCarousel = (props: SpaceCarouselProps & StaticCarouselProps) => (
	<ItemsCarousel {...props} />
)

const DynamicCarousel = ({
	filters,
	...props
}: SpaceCarouselProps & DynamicCarouselProps) => {
	const { data: spaces } = useListSpaces(filters)

	return (
		<ItemsCarousel
			{...props}
			spaces={spaces?.data || []}
		/>
	)
}

export const SpaceCarousel = (props: SpaceCarouselProps) => {
	if ('spaces' in props) return <StaticCarousel {...props} />

	return <DynamicCarousel {...props} />
}
